import React, { useState } from "react";
import { Form, Input, Button } from "antd";
import { LockOutlined, CheckCircleFilled } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { confirmPasswordReset } from "Common/Auth/authService";
import "./auth.css";

const ResetPassword = () => {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      await confirmPasswordReset(token, values.password);
      setDone(true);
      toast.success("Your password has been reset.");
      setTimeout(() => navigate("/login"), 2500);
    } catch (error) {
      const data = error.response?.data;
      const msg =
        (typeof data === "string" && data) ||
        data?.message ||
        data?.title ||
        "This reset link is invalid or has expired. Please request a new one.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const brandPanel = (
    <aside className="ciq-auth__brand">
      <div className="ciq-auth__brand-bg" />
      <div className="ciq-auth__brand-inner">
        <a className="ciq-brand" href="https://coreiqpro.com">
          <img src={`${process.env.PUBLIC_URL}/logo-icon.png`} alt="CoreIQPro" />
          <span className="ciq-brand__txt">
            Core<span className="o">IQ</span>Pro
          </span>
        </a>
        <h1 className="ciq-auth__headline">Set a new password.</h1>
        <p className="ciq-auth__sub">
          Choose a strong password to keep your CoreIQPro account secure.
        </p>
        <ul className="ciq-auth__points">
          <li>
            <i className="fas fa-check" /> At least 8 characters
          </li>
          <li>
            <i className="fas fa-check" /> Use something you do not reuse elsewhere
          </li>
          <li>
            <i className="fas fa-check" /> Takes just a moment
          </li>
        </ul>
      </div>
    </aside>
  );

  return (
    <div className="ciq-auth">
      {brandPanel}
      <main className="ciq-auth__panel">
        <motion.div
          className="ciq-auth__card"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="ciq-auth__card-head">
            <div>
              <h2>Set a new password</h2>
              <p className="ciq-auth__muted">Choose a strong password for your account.</p>
            </div>
          </div>

          {!token ? (
            <>
              <div className="ciq-auth__msg error">
                <div className="ciq-auth__msg-title">Invalid link</div>
                This password reset link is missing or invalid. Please request a new one.
              </div>
              <div className="ciq-auth__foot">
                <Button type="link" onClick={() => navigate("/forgot-password")}>
                  Request a new link
                </Button>
              </div>
            </>
          ) : done ? (
            <>
              <div className="ciq-auth__msg success">
                <div className="ciq-auth__msg-title">
                  <CheckCircleFilled /> Password reset
                </div>
                Your password has been updated. Redirecting you to Sign In...
              </div>
              <div className="ciq-auth__foot">
                <Button type="link" onClick={() => navigate("/login")}>
                  Go to Sign In
                </Button>
              </div>
            </>
          ) : (
            <Form name="reset-form" layout="vertical" onFinish={handleSubmit} requiredMark={false}>
              <Form.Item
                name="password"
                label="New password"
                hasFeedback
                rules={[
                  { required: true, message: "Please enter a new password!" },
                  { min: 8, message: "Password must be at least 8 characters!" },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="confirm"
                label="Confirm password"
                dependencies={["password"]}
                hasFeedback
                rules={[
                  { required: true, message: "Please confirm your password!" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error("Passwords do not match"));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Re-enter new password"
                  autoComplete="new-password"
                  size="large"
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
                <motion.div
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: "spring", stiffness: 400, damping: 20 }}
                >
                  <Button type="primary" htmlType="submit" block loading={loading} disabled={loading}>
                    {loading ? "Resetting..." : "Reset password"}
                  </Button>
                </motion.div>
              </Form.Item>
            </Form>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default ResetPassword;
